import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S4: Internal Sync Endpoint — Fallback for when Cegid/n8n is not configured.
 * Aggregates data from documentos (estado='contabilizado') into reporting tables:
 *   - datos_financieros (ingresos, gastos, resultado, margen per period)
 *   - tax_summary (IVA aggregates per period)
 *   - cegid_sync_runs (audit log with sync_type='internal')
 *
 * POST /api/dashboard/internal-sync
 * Body: { clienteId?: string }  — if omitted, processes ALL clients
 */

interface DatosIA {
  total?: string | number;
  totalFactura?: string | number;
  importeTotal?: string | number;
  baseImponible?: string | number;
  base_imponible?: string | number;
  cuotaIva?: string | number;
  cuota_iva?: string | number;
  porcentajeIva?: string | number;
  porcentaje_iva?: string | number;
  fechaFactura?: string;
  fecha?: string;
  nivelConfianza?: number;
}

interface DocumentRow {
  id: string;
  cliente_id: string;
  tipo: string;
  estado: string;
  fecha_subida: string;
  datos_ia: DatosIA | null;
}

interface PeriodAggregation {
  ingresos: number;
  gastos: number;
  iva_repercutido: number;
  iva_soportado: number;
  base_emitidas: number;
  base_recibidas: number;
  num_emitidas: number;
  num_recibidas: number;
}

interface ClientError {
  clienteId: string;
  error: string;
}

function extractNumeric(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
}

function getPeriodoFromDate(dateStr: string | undefined | null): string {
  let date: Date;
  if (dateStr) {
    date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      date = new Date();
    }
  } else {
    date = new Date();
  }
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

function determineRisk(margen: number): 'bajo' | 'medio' | 'alto' {
  if (margen < 0) return 'alto';
  if (margen < 5) return 'medio';
  return 'bajo';
}

async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  _user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();
  const startTime = Date.now();
  const { clienteId } = (req.body || {}) as { clienteId?: string };

  // ── 1. Create sync run record ──────────────────────────────────────────
  const { data: syncRun, error: syncRunError } = await supabaseAdmin
    .from('cegid_sync_runs')
    .insert({
      sync_type: 'internal',
      status: 'running',
      started_at: new Date().toISOString(),
      metadata: {
        triggered_by: 'internal_sync_endpoint',
        cliente_filter: clienteId || 'all',
      },
    })
    .select('id')
    .single();

  if (syncRunError || !syncRun) {
    return res.status(500).json({
      success: false,
      error: 'Failed to create sync run record',
      details: syncRunError?.message,
    });
  }

  const syncRunId: string = syncRun.id;
  let totalRecordsProcessed = 0;
  let totalRecordsSuccess = 0;
  let totalRecordsFailed = 0;
  let clientesProcessed = 0;
  const clientErrors: ClientError[] = [];

  try {
    // ── 2. Fetch target clients ────────────────────────────────────────────
    let clienteIds: string[] = [];

    if (clienteId) {
      clienteIds = [clienteId];
    } else {
      const { data: clientes, error: clientesError } = await supabaseAdmin
        .from('clientes')
        .select('id');

      if (clientesError) {
        throw new Error(`Failed to fetch clients: ${clientesError.message}`);
      }

      clienteIds = (clientes || []).map((c: { id: string }) => c.id);
    }

    if (clienteIds.length === 0) {
      // No clients — mark sync as completed with 0 records
      const durationMs = Date.now() - startTime;
      await supabaseAdmin
        .from('cegid_sync_runs')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          duration_ms: durationMs,
          records_processed: 0,
          records_success: 0,
          records_failed: 0,
        })
        .eq('id', syncRunId);

      return res.status(200).json({
        success: true,
        syncRunId,
        clientesProcessed: 0,
        recordsUpserted: 0,
        durationMs,
      });
    }

    // ── 3. Process each client ─────────────────────────────────────────────
    for (const cId of clienteIds) {
      try {
        // Fetch all contabilizado documents for this client
        const { data: documentos, error: docError } = await supabaseAdmin
          .from('documentos')
          .select('id, cliente_id, tipo, estado, fecha_subida, datos_ia')
          .eq('cliente_id', cId)
          .eq('estado', 'contabilizado');

        if (docError) {
          throw new Error(`Failed to fetch documents: ${docError.message}`);
        }

        const docs = (documentos || []) as DocumentRow[];

        if (docs.length === 0) {
          // No contabilizado docs for this client — skip
          clientesProcessed++;
          continue;
        }

        // ── 3a. Group documents by periodo ───────────────────────────────
        const periodMap = new Map<string, PeriodAggregation>();

        for (const doc of docs) {
          totalRecordsProcessed++;

          // Determine period: prefer datos_ia date, fallback to fecha_subida
          const datosIA = doc.datos_ia;
          const invoiceDate = datosIA?.fechaFactura || datosIA?.fecha || doc.fecha_subida;
          const periodo = getPeriodoFromDate(invoiceDate);

          if (!periodMap.has(periodo)) {
            periodMap.set(periodo, {
              ingresos: 0,
              gastos: 0,
              iva_repercutido: 0,
              iva_soportado: 0,
              base_emitidas: 0,
              base_recibidas: 0,
              num_emitidas: 0,
              num_recibidas: 0,
            });
          }

          const agg = periodMap.get(periodo)!;

          // Determine if emitida (income) or recibida (expense)
          const isEmitida = doc.tipo === 'emitida' ||
            doc.tipo === 'emitida_pdf' ||
            doc.tipo === 'emitida_excel';
          const isRecibida = doc.tipo === 'recibida' ||
            doc.tipo === 'recibida_pdf' ||
            doc.tipo === 'recibida_excel';

          // Extract financial amounts from datos_ia with fallback
          const total = extractNumeric(
            datosIA?.total ?? datosIA?.totalFactura ?? datosIA?.importeTotal
          );
          const baseImponible = extractNumeric(
            datosIA?.baseImponible ?? datosIA?.base_imponible
          );
          const cuotaIva = extractNumeric(
            datosIA?.cuotaIva ?? datosIA?.cuota_iva
          );

          if (isEmitida) {
            agg.ingresos += total || baseImponible;
            agg.iva_repercutido += cuotaIva;
            agg.base_emitidas += baseImponible || total;
            agg.num_emitidas++;
          } else if (isRecibida) {
            agg.gastos += total || baseImponible;
            agg.iva_soportado += cuotaIva;
            agg.base_recibidas += baseImponible || total;
            agg.num_recibidas++;
          } else {
            // Unknown tipo — count as expense by default (conservative)
            agg.gastos += total || baseImponible;
            agg.iva_soportado += cuotaIva;
            agg.base_recibidas += baseImponible || total;
            agg.num_recibidas++;
          }
        }

        // ── 3b. Upsert datos_financieros per period ──────────────────────
        for (const [periodo, agg] of periodMap.entries()) {
          try {
            const resultado = agg.ingresos - agg.gastos;
            const margen = agg.ingresos > 0
              ? (resultado / agg.ingresos) * 100
              : 0;

            const { error: upsertFinError } = await supabaseAdmin
              .from('datos_financieros')
              .upsert(
                {
                  cliente_id: cId,
                  periodo,
                  ingresos: agg.ingresos,
                  gastos: agg.gastos,
                  resultado,
                  margen,
                  riesgo_ia: determineRisk(margen),
                  ultimo_cierre: new Date().toISOString(),
                },
                {
                  onConflict: 'cliente_id,periodo',
                  ignoreDuplicates: false,
                }
              );

            if (upsertFinError) {
              throw new Error(
                `datos_financieros upsert failed for ${periodo}: ${upsertFinError.message}`
              );
            }

            totalRecordsSuccess++;
          } catch (upsertErr) {
            totalRecordsFailed++;
            const msg = upsertErr instanceof Error ? upsertErr.message : String(upsertErr);
            console.error(
              `[internal-sync] datos_financieros error for client=${cId} period=${periodo}:`,
              msg
            );
          }
        }

        // ── 3c. Upsert tax_summary per period ───────────────────────────
        for (const [periodo, agg] of periodMap.entries()) {
          try {
            const year = periodo.split('-')[0];

            const { error: upsertTaxError } = await supabaseAdmin
              .from('tax_summary')
              .upsert(
                {
                  cliente_id: cId,
                  periodo,
                  ejercicio_fiscal: year,
                  iva_repercutido: agg.iva_repercutido,
                  iva_soportado: agg.iva_soportado,
                  iva_resultado: agg.iva_repercutido - agg.iva_soportado,
                  base_imponible_emitidas: agg.base_emitidas,
                  base_imponible_recibidas: agg.base_recibidas,
                  retenciones_practicadas: 0,
                  retenciones_soportadas: 0,
                  total_suplidos: 0,
                  num_facturas_emitidas: agg.num_emitidas,
                  num_facturas_recibidas: agg.num_recibidas,
                  source: 'internal_sync',
                  last_sync_at: new Date().toISOString(),
                },
                {
                  onConflict: 'cliente_id,periodo',
                  ignoreDuplicates: false,
                }
              );

            if (upsertTaxError) {
              throw new Error(
                `tax_summary upsert failed for ${periodo}: ${upsertTaxError.message}`
              );
            }

            totalRecordsSuccess++;
          } catch (upsertErr) {
            totalRecordsFailed++;
            const msg = upsertErr instanceof Error ? upsertErr.message : String(upsertErr);
            console.error(
              `[internal-sync] tax_summary error for client=${cId} period=${periodo}:`,
              msg
            );
          }
        }

        clientesProcessed++;
      } catch (clientErr) {
        const msg = clientErr instanceof Error ? clientErr.message : String(clientErr);
        clientErrors.push({ clienteId: cId, error: msg });
        console.error(`[internal-sync] Error processing client ${cId}:`, msg);
        clientesProcessed++;
      }
    }

    // ── 4. Finalize sync run ──────────────────────────────────────────────
    const durationMs = Date.now() - startTime;
    const finalStatus =
      totalRecordsFailed === 0 && clientErrors.length === 0
        ? 'completed'
        : totalRecordsSuccess > 0
          ? 'completed'
          : 'failed';

    await supabaseAdmin
      .from('cegid_sync_runs')
      .update({
        status: finalStatus,
        completed_at: new Date().toISOString(),
        duration_ms: durationMs,
        records_processed: totalRecordsProcessed,
        records_success: totalRecordsSuccess,
        records_failed: totalRecordsFailed,
        error_message:
          clientErrors.length > 0
            ? clientErrors.map((e) => `${e.clienteId}: ${e.error}`).join('; ')
            : null,
      })
      .eq('id', syncRunId);

    return res.status(200).json({
      success: true,
      syncRunId,
      clientesProcessed,
      recordsUpserted: totalRecordsSuccess,
      durationMs,
      ...(clientErrors.length > 0 ? { errors: clientErrors } : {}),
    });
  } catch (error) {
    // ── 5. Fatal error — mark sync run as failed ─────────────────────────
    const durationMs = Date.now() - startTime;
    const errorMessage = error instanceof Error ? error.message : String(error);

    await supabaseAdmin
      .from('cegid_sync_runs')
      .update({
        status: 'failed',
        completed_at: new Date().toISOString(),
        duration_ms: durationMs,
        records_processed: totalRecordsProcessed,
        records_success: totalRecordsSuccess,
        records_failed: totalRecordsFailed,
        error_message: errorMessage,
      })
      .eq('id', syncRunId);

    return res.status(500).json({
      success: false,
      syncRunId,
      error: 'Internal sync failed',
      details: errorMessage,
      clientesProcessed,
      recordsUpserted: totalRecordsSuccess,
      durationMs,
    });
  }
}

export default withAuth(handler, ['admin']);
